const PRO_TIPS = [
  {
    tip: "Stick to a 180-degree shutter angle rule (shutter speed double the frame rate).",
    reason: "This creates natural cinematic motion blur and prevents choppy movement.",
  },
  {
    tip: "Maintain a consistent frame rate across all generated assets, such as 24fps.",
    reason: "Consistent frame rates prevent unnatural pacing and frame jitter during editing.",
  },
  {
    tip: "Frame subject actions with standard cinematic margins such as headroom and lead room.",
    reason: "Proper spacing prevents subjects from feeling cramped or awkwardly cropped.",
  },
  {
    tip: "Lock the focus plane for static shots.",
    reason: "This prevents visual breathing and focus hunting artifacts.",
  },
  {
    tip: "Avoid extremely wide fisheye focal lengths unless explicitly required.",
    reason: "Extreme distortion can make edge continuity and subject proportions unstable.",
  },
  {
    tip: "Use standard focal length equivalents such as 35mm, 50mm, or 85mm.",
    reason: "Standard focal lengths provide clearer perspective and depth references.",
  },
  {
    tip: "Keep horizon lines level across continuous scene shots.",
    reason: "Consistent horizons prevent distracting perspective jumps between clips.",
  },
  {
    tip: "Use a shallow depth of field around f/1.8–f/2.8 for close-ups.",
    reason: "It separates the subject from the background and emphasizes facial detail.",
  },
  {
    tip: "Use a deep depth of field around f/8–f/11 for expansive landscapes.",
    reason: "This keeps environmental details sharp throughout the frame.",
  },
  {
    tip: "Prefer eye-level camera placement for natural narrative dialogue.",
    reason: "Eye-level framing creates a grounded and natural relationship with the viewer.",
  },
  {
    tip: "Reserve low camera angles for scenes requiring power or scale.",
    reason: "Low angles visually increase the presence and perceived scale of a subject.",
  },
  {
    tip: "Reserve high angles for vulnerability or spatial overview.",
    reason: "High angles naturally reduce subject dominance within the environment.",
  },
  {
    tip: "Use rack focus transitions sparingly within single clips.",
    reason: "AI video engines can produce blurry artifacts during focal transitions.",
  },
  {
    tip: "Generate footage in the native aspect ratio required by the target platform.",
    reason: "Aggressive cropping or upscaling can damage composition and image detail.",
  },
  {
    tip: "Avoid erratic multi-axis camera movements in a single prompt.",
    reason: "Complex camera paths can cause visual warping and structural instability.",
  },
  {
    tip: "Use simple camera movement vectors such as left-to-right tracking or push-ins.",
    reason: "Linear camera paths are generally easier for motion engines to interpret.",
  },
  {
    tip: "Specify focal distance when describing important spatial relationships.",
    reason: "It helps the generator distinguish foreground, subject, and background layers.",
  },
  {
    tip: "Specify camera movement speed, such as slow glide or fast whip.",
    reason: "Movement speed controls the energy and cadence of the generated shot.",
  },
  {
    tip: "Keep digital sensor grain off during generation.",
    reason: "Clean renders allow more controlled grain application during post-production.",
  },

  // Lighting & Exposure
  {
    tip: "Use three-point key, fill, and rim lighting setups.",
    reason: "This creates dimensional subject definition and clean separation from backgrounds.",
  },
  {
    tip: "Maintain soft, diffused lighting for realistic portraiture.",
    reason: "Soft light produces smoother highlights and more natural facial rendering.",
  },
  {
    tip: "Keep the light source direction consistent across sequential shots.",
    reason: "Consistent shadows preserve spatial realism between camera angles.",
  },
  {
    tip: "Specify ambient color temperature, such as 5600K daylight or 3200K tungsten.",
    reason: "Color temperature helps maintain lighting continuity across generated assets.",
  },
  {
    tip: "Use rim lighting to separate silhouettes in dark environments.",
    reason: "It prevents subjects from disappearing into dark backgrounds.",
  },
  {
    tip: "Avoid overexposing bright highlights in scene prompts.",
    reason: "Blown highlights lose visual texture that cannot be recovered later.",
  },
  {
    tip: "Use motivated practical lights inside interior scenes.",
    reason: "Lamps and windows provide believable justification for lighting direction.",
  },
  {
    tip: "Use high-contrast side lighting for dramatic scenes.",
    reason: "Chiaroscuro lighting increases visual tension and dimensionality.",
  },
  {
    tip: "Soften shadows with bounce surfaces or ambient fill.",
    reason: "This preserves dark-area detail without completely flattening contrast.",
  },
  {
    tip: "Avoid unnecessarily mixing opposing color temperatures.",
    reason: "Mixed lighting can complicate white balance and secondary color correction.",
  },
  {
    tip: "Use golden-hour lighting for warm, cinematic outdoor scenes.",
    reason: "Low-angle sunlight creates long shadows, warmth, and depth.",
  },
  {
    tip: "Use blue-hour lighting for quiet and atmospheric scenes.",
    reason: "Cool ambient tones create a calm and cinematic mood.",
  },
  {
    tip: "Control specular reflection intensity on reflective surfaces.",
    reason: "Excessive glare can create visual noise and unstable renders.",
  },
  {
    tip: "Use soft volumetric fog or haze in wide environmental shots.",
    reason: "Atmospheric depth helps visually separate foreground and distant layers.",
  },
  {
    tip: "Keep lighting contrast consistent across coverage angles.",
    reason: "Sudden contrast changes can break visual continuity during edits.",
  },
  {
    tip: "Avoid uncontrolled light-level flickering.",
    reason: "Abrupt lighting changes can destabilize temporal consistency.",
  },
  {
    tip: "Use overcast lighting for soft and evenly distributed exposure.",
    reason: "Cloudy conditions provide a stable baseline for color grading.",
  },
  {
    tip: "Specify edge lighting for background foliage and architecture.",
    reason: "Edge light helps intricate environmental details remain visually separated.",
  },
  {
    tip: "Avoid pure black shadow regions without ambient detail.",
    reason: "Retaining basic shadow information improves visual depth and recoverability.",
  },
  {
    tip: "Standardize fill-light intensity across dialogue scenes.",
    reason: "Consistent illumination keeps conversation coverage visually coherent.",
  },

  // Character & Subject Design
  {
    tip: "Maintain distinct character features such as hair, eyes, and skin tone.",
    reason: "Consistent physical traits are essential for character identity continuity.",
  },
  {
    tip: "Keep wardrobe choices simple and visually distinct per character.",
    reason: "Complex clothing can shift shape or warp between generated frames.",
  },
  {
    tip: "Avoid high-frequency clothing patterns such as fine grids or houndstooth.",
    reason: "Dense patterns can create moiré and temporal flickering.",
  },
  {
    tip: "Match character costume styling directly to the narrative setting.",
    reason: "Contextually appropriate clothing improves visual immersion.",
  },
  {
    tip: "Specify character age ranges clearly in physical descriptions.",
    reason: "Clear age references help prevent facial structure and age inconsistencies.",
  },
  {
    tip: "Standardize hair length and hairstyle descriptions across prompts.",
    reason: "Hair can otherwise change length, shape, or texture between generations.",
  },
  {
    tip: "Limit the number of primary characters in a single active shot.",
    reason: "Multiple characters increase the risk of identity and action confusion.",
  },
  {
    tip: "Give important characters distinct visual silhouettes.",
    reason: "Strong silhouettes make subjects easier to identify in wide shots.",
  },
  {
    tip: "Prefer matte wardrobe fabrics over highly reflective materials.",
    reason: "Matte surfaces produce more stable highlights during movement.",
  },
  {
    tip: "Define character eye color consistently.",
    reason: "Eye color can shift noticeably between close-up generations.",
  },
  {
    tip: "Avoid complex jewelry and tiny accessories on active characters.",
    reason: "Small details can glitch or drift during rapid movement.",
  },
  {
    tip: "Describe facial structure clearly for important recurring characters.",
    reason: "Structural definitions help reduce facial morphing.",
  },
  {
    tip: "Keep makeup styles natural and consistent.",
    reason: "Large makeup changes can create inconsistent skin textures across cuts.",
  },
  {
    tip: "Define character stature relative to the surrounding environment.",
    reason: "Relative scale descriptions reduce character size inconsistencies.",
  },
  {
    tip: "Limit each character's wardrobe to two or three primary colors.",
    reason: "Simple palettes reduce color bleeding into the environment.",
  },
  {
    tip: "Avoid rapid wardrobe changes within continuous-time scenes.",
    reason: "Consistent clothing preserves temporal visual logic.",
  },
  {
    tip: "Keep facial grooming descriptions consistent across scene prompts.",
    reason: "Facial hair and grooming details can otherwise vary between generations.",
  },
  {
    tip: "Prefer structured garments such as jackets or structured shirts.",
    reason: "Structured clothing maintains a more stable shape during movement.",
  },
  {
    tip: "Use timeless visual styling for recurring character descriptions.",
    reason: "It reduces unnecessary styling variations between generations.",
  },
  {
    tip: "Describe the intended facial expression explicitly.",
    reason: "Clear emotional direction prevents characters from appearing expressionless.",
  },

  // Environment & World Building
  {
    tip: "Define specific architectural styles for background structures.",
    reason: "Clear architectural references create a believable visual world.",
  },
  {
    tip: "Keep environmental scale proportional to human subjects.",
    reason: "Correct proportions prevent distorted doors, buildings, and spatial relationships.",
  },
  {
    tip: "Specify weather conditions clearly.",
    reason: "Weather affects lighting, reflections, atmosphere, and scene continuity.",
  },
  {
    tip: "Use consistent architectural materials across shots of the same location.",
    reason: "Material consistency strengthens geographic and visual continuity.",
  },
  {
    tip: "Avoid overly chaotic background crowds.",
    reason: "Dense crowds can introduce limb anomalies and unwanted visual movement.",
  },
  {
    tip: "Limit background movement to subtle natural elements.",
    reason: "Controlled background motion keeps attention on the primary subject.",
  },
  {
    tip: "Match environmental lighting direction to the background light source.",
    reason: "Consistent light direction prevents spatial contradictions.",
  },
  {
    tip: "Use realistic scale ratios for interior spaces.",
    reason: "Consistent proportions prevent rooms from changing size between shots.",
  },
  {
    tip: "Use clean and clearly defined ground textures.",
    reason: "Ground surfaces are common areas for AI motion artifacts.",
  },
  {
    tip: "Keep background colors complementary to character wardrobe colors.",
    reason: "Complementary palettes help subjects stand out clearly.",
  },
  {
    tip: "Avoid large reflective surfaces such as mirrors when unnecessary.",
    reason: "Reflections introduce additional geometry that can flicker or distort.",
  },
  {
    tip: "Define clear spatial landmarks in wide location shots.",
    reason: "Landmarks help viewers maintain spatial orientation across cuts.",
  },
  {
    tip: "Specify the time of day consistently across connected scenes.",
    reason: "This prevents unwanted changes in sunlight and ambient exposure.",
  },
  {
    tip: "Keep vegetation density realistic and structurally organized.",
    reason: "Overly dense foliage descriptions can produce chaotic background shapes.",
  },
  {
    tip: "Limit background traffic movement speed in city scenes.",
    reason: "Fast vehicles can introduce motion ghosting and distortion.",
  },
  {
    tip: "Use natural horizon lines in open landscape shots.",
    reason: "Natural horizons provide stable perspective and composition.",
  },
  {
    tip: "Match interior ceiling height with the exterior building scale.",
    reason: "Consistent dimensions maintain structural continuity.",
  },
  {
    tip: "Keep background props minimal and relevant.",
    reason: "Too many objects increase morphing and visual distraction.",
  },
  {
    tip: "Describe background wall textures explicitly.",
    reason: "Specific surfaces reduce unwanted texture changes.",
  },
  {
    tip: "Align perspective grid lines in architectural prompts.",
    reason: "Consistent vanishing points help keep structures visually grounded.",
  },

  // Motion & Action Control
  {
    tip: "Limit each prompt sequence to one primary character action.",
    reason: "Fewer simultaneous actions reduce motion-generation errors.",
  },
  {
    tip: "Prefer slow and deliberate character movements.",
    reason: "Slower movement generally produces smoother temporal frames.",
  },
  {
    tip: "Use clear directional movement descriptors such as left-to-right.",
    reason: "Directional vectors help the generator understand movement paths.",
  },
  {
    tip: "Avoid high-speed acrobatics and complex physical flips.",
    reason: "Rapid rotations frequently produce limb and anatomy distortions.",
  },
  {
    tip: "Keep character interaction with objects simple.",
    reason: "Complex hand-object interactions are prone to clipping and tracking errors.",
  },
  {
    tip: "Maintain consistent motion direction across sequential camera cuts.",
    reason: "Consistent screen direction prevents confusing editorial continuity.",
  },
  {
    tip: "Add subtle body micro-movements to idle character shots.",
    reason: "Small movements prevent characters from appearing completely frozen.",
  },
  {
    tip: "Avoid sudden 180-degree subject turns within one generation pass.",
    reason: "Large rotations can cause facial reconstruction failures.",
  },
  {
    tip: "Keep head movements steady during important dialogue frames.",
    reason: "Stable facial geometry improves tracking and lip-sync reliability.",
  },
  {
    tip: "Use natural gravity for falling or moving props.",
    reason: "Believable physics prevents objects from appearing weightless.",
  },
  {
    tip: "Control background motion speed relative to camera movement.",
    reason: "Correct parallax requires believable relative movement speeds.",
  },
  {
    tip: "Avoid rapid hand gestures close to the face.",
    reason: "Hands near facial geometry can cause tracking and merging artifacts.",
  },
  {
    tip: "Use continuous forward motion vectors for vehicle shots.",
    reason: "Consistent movement helps maintain realistic road and wheel motion.",
  },
  {
    tip: "Keep character footsteps within visible frame boundaries.",
    reason: "Visible feet help the engine calculate weight distribution and reduce sliding.",
  },
  {
    tip: "Limit simultaneous movement in opposing directions.",
    reason: "Opposing motion increases the risk of temporal breakdown.",
  },
  {
    tip: "Smooth camera acceleration at the beginning and end of movements.",
    reason: "Sudden starts and stops can create visual jerks.",
  },
  {
    tip: "Match subject movement speed to the emotional beat of the scene.",
    reason: "Motion energy reinforces the intended dramatic tone.",
  },
  {
    tip: "Avoid extreme ragdoll-style physics prompts.",
    reason: "They can produce unnatural skeletal deformation.",
  },
  {
    tip: "Give characters simple anchor props for their hands.",
    reason: "Holding stable objects or using pockets can reduce hand artifacts.",
  },
  {
    tip: "Specify movement acceleration when it matters.",
    reason: "Acceleration cues help create believable weight transfer.",
  },

  // Visual Consistency & Continuity
  {
    tip: "Use image-to-video reference anchors for recurring subjects.",
    reason: "Reference images help preserve character and environment identity.",
  },
  {
    tip: "Keep lighting style consistent across all coverage shots.",
    reason: "Consistent lighting prevents distracting visual jumps.",
  },
  {
    tip: "Match focal lengths across reverse dialogue coverage.",
    reason: "Matching lenses preserves facial proportions and background compression.",
  },
  {
    tip: "Maintain identical clothing colors across connected shots.",
    reason: "Color changes between cuts immediately weaken continuity.",
  },
  {
    tip: "Preserve prop placement between wide and close camera setups.",
    reason: "Stable prop positions prevent objects from appearing to jump.",
  },
  {
    tip: "Use consistent color-management settings across generated assets.",
    reason: "Consistent color handling prevents gamma and gamut shifts.",
  },
  {
    tip: "Keep weather effects identical throughout a single scene sequence.",
    reason: "Changing weather breaks temporal continuity.",
  },
  {
    tip: "Use the same character reference assets across sequence prompts.",
    reason: "Consistent references are essential for identity fidelity.",
  },
  {
    tip: "Keep background architecture consistent across reverse angles.",
    reason: "Stable spatial geometry keeps the scene believable.",
  },
  {
    tip: "Match eye-line height across dialogue cuts.",
    reason: "Correct eye lines preserve believable character relationships.",
  },
  {
    tip: "Keep subject scale relative to environmental elements consistent.",
    reason: "Stable proportions prevent characters from appearing to change size.",
  },
  {
    tip: "Use identical depth-of-field parameters for matching shot types.",
    reason: "Consistent focus hierarchy improves editorial continuity.",
  },
  {
    tip: "Preserve shadow direction across coverage angles.",
    reason: "Changing shadows can reveal separately generated footage.",
  },
  {
    tip: "Keep hair placement and style consistent across sequence prompts.",
    reason: "Unexpected hairstyle changes disrupt visual continuity.",
  },
  {
    tip: "Maintain consistent grain structure during post-processing.",
    reason: "Uniform grain helps separate generated shots feel like one medium.",
  },
  {
    tip: "Keep facial proportions consistent across camera angles.",
    reason: "Stable facial structure improves character recognition.",
  },
  {
    tip: "Match surface wetness and sheen across continuous outdoor shots.",
    reason: "Consistent surface conditions preserve weather continuity.",
  },
  {
    tip: "Keep vehicle design identical across driving sequence shots.",
    reason: "Vehicle details can otherwise change between generations.",
  },
  {
    tip: "Balance ambient light between indoor and outdoor views through windows.",
    reason: "Consistent exposure keeps the scene visually grounded.",
  },
  {
    tip: "Standardize subject-to-camera distances for recurring character framing.",
    reason: "Stable distances provide more consistent editorial pacing.",
  },

  // Prompting & Generation Logic
  {
    tip: "Lead prompts with the primary subject and main camera action.",
    reason: "Important instructions placed early can receive stronger model attention.",
  },
  {
    tip: "Use precise cinematic terms instead of vague emotional buzzwords.",
    reason: "Technical terms provide clearer spatial and camera instructions.",
  },
  {
    tip: "Use negative prompts where the selected generation model supports them.",
    reason: "They can help suppress common artifacts and unwanted visual elements.",
  },
  {
    tip: "Keep prompt sentences structured without conflicting adjectives.",
    reason: "Contradictory descriptions can confuse generation behavior.",
  },
  {
    tip: "Separate visual descriptions from motion instructions.",
    reason: "Clear structure helps distinguish scene design from movement.",
  },
  {
    tip: "Use specific color names such as terracotta instead of generic orange.",
    reason: "Specific color references provide more controlled visual output.",
  },
  {
    tip: "Avoid unnecessary prompt overload.",
    reason: "Too many instructions can dilute the most important scene requirements.",
  },
  {
    tip: "Specify lighting style explicitly.",
    reason: "Clear lighting instructions prevent generic model defaults.",
  },
  {
    tip: "Describe camera movement using standard film terminology.",
    reason: "Terms such as dolly-in and pan-right provide precise movement references.",
  },
  {
    tip: "Describe materials using physical textures.",
    reason: "Concrete material descriptions produce more predictable surfaces.",
  },
  {
    tip: "Avoid relying on vague quality descriptors.",
    reason: "Concrete visual instructions are more useful than generic quality claims.",
  },
  {
    tip: "Anchor complex scenes with clear environmental descriptions first.",
    reason: "Spatial context helps organize subsequent subject and motion instructions.",
  },
  {
    tip: "Specify the desired dynamic-range characteristics.",
    reason: "Terms such as soft midtones or high-contrast shadows guide tonal structure.",
  },
  {
    tip: "Use precise spatial measurements when object placement matters.",
    reason: "Measurements can improve relative positioning within generated scenes.",
  },
  {
    tip: "Rephrase prompts when an artifact repeatedly appears.",
    reason: "Changing prompt structure can produce a different generation pathway.",
  },
  {
    tip: "Keep historical time-period descriptions accurate.",
    reason: "Accurate context reduces anachronistic props and architecture.",
  },
  {
    tip: "Specify aperture settings when depth of field is important.",
    reason: "Photographic aperture references help guide background blur.",
  },
  {
    tip: "Use simple action verbs for character movement.",
    reason: "Simple verbs reduce ambiguous motion interpretation.",
  },
  {
    tip: "Define the primary color scheme before detailed scene instructions.",
    reason: "Early palette definition helps establish overall visual harmony.",
  },
  {
    tip: "Prefer positive descriptions over direct negation when possible.",
    reason: "Some generators may interpret negation inconsistently.",
  },

  // Compositing & Post-Production
  {
    tip: "Export generated renders at their maximum native bitrate before editing.",
    reason: "Higher-quality source files preserve more image information.",
  },
  {
    tip: "Perform primary color correction before applying creative grades.",
    reason: "Correcting exposure and white balance first creates a stable grading base.",
  },
  {
    tip: "Apply subtle film grain to final composite renders.",
    reason: "Fine grain can unify layers and mask minor generation inconsistencies.",
  },
  {
    tip: "Sharpen selectively around eyes and important focal points.",
    reason: "Selective sharpening improves clarity without amplifying background noise.",
  },
  {
    tip: "Use optical-flow retiming for controlled slow-motion conversions.",
    reason: "Frame interpolation can create smoother motion when handled carefully.",
  },
  {
    tip: "Match noise profiles across clips from different generation passes.",
    reason: "Consistent texture makes separately generated shots feel more cohesive.",
  },
  {
    tip: "Use matte keying for clean background replacements.",
    reason: "Accurate mattes help prevent edge bleed around subjects.",
  },
  {
    tip: "Correct lens distortion on wide generated shots before VFX integration.",
    reason: "Corrected geometry makes tracking and compositing more reliable.",
  },
  {
    tip: "Use subtle vignette effects when they support the composition.",
    reason: "A restrained vignette can guide attention toward the primary subject.",
  },
  {
    tip: "Use localized masks for isolated color corrections.",
    reason: "Local corrections prevent clean areas from being unnecessarily altered.",
  },
  {
    tip: "Smooth tracking curves during stabilization.",
    reason: "Clean curves reduce jitter without introducing excessive warping.",
  },
  {
    tip: "Use chromatic aberration sparingly.",
    reason: "Small amounts can mimic optical characteristics without degrading image quality.",
  },
  {
    tip: "Match black levels across cuts during final color mastering.",
    reason: "Consistent black levels unify contrast between generated shots.",
  },
  {
    tip: "Retain clean alpha channels when exporting keyable assets.",
    reason: "Clean transparency prevents contamination when layering elements.",
  },
  {
    tip: "Use motion vectors for controlled post-applied motion blur.",
    reason: "Motion vectors allow blur to follow actual movement direction.",
  },
  {
    tip: "Denoise generated footage before heavy tracking or keying.",
    reason: "Cleaner footage generally produces more reliable tracking results.",
  },
  {
    tip: "Match skin tones across character close-ups.",
    reason: "Skin-tone shifts are especially noticeable between consecutive shots.",
  },
  {
    tip: "Use a consistent color-space pipeline across the project.",
    reason: "Consistent color management prevents unexpected rendering differences.",
  },
  {
    tip: "Apply motion blur to composited tracking assets.",
    reason: "Matching blur makes inserted elements blend naturally with footage.",
  },
  {
    tip: "Export final masters in high-fidelity formats when appropriate.",
    reason: "High-quality masters reduce generational quality loss.",
  },

  // VFX, Retouching & Clean-up
  {
    tip: "Fix temporal flickering with controlled frame-blending cleanup passes.",
    reason: "Cleanup can stabilize uneven brightness or texture changes.",
  },
  {
    tip: "Correct extra or distorted fingers with localized replacement masks.",
    reason: "Localized corrections can fix anatomy without regenerating the entire shot.",
  },
  {
    tip: "Replace problematic background elements using tracking and clean plates.",
    reason: "Clean plates provide controlled background replacement.",
  },
  {
    tip: "Fix floating or unnatural props with localized retouching.",
    reason: "Targeted corrections restore believable object placement.",
  },
  {
    tip: "Smooth unnatural skin-texture warping with tracked cleanup passes.",
    reason: "Subtle corrections preserve facial structure while reducing artifacts.",
  },
  {
    tip: "Stabilize jittery background details with tracked static regions.",
    reason: "Background locking can prevent distracting environmental movement.",
  },
  {
    tip: "Remove unwanted background artifacts using patch cloning.",
    reason: "Cloning can quickly clean distracting elements while preserving texture.",
  },
  {
    tip: "Use paint-fix passes to clean warped subject edges.",
    reason: "Paint fixes restore cleaner boundaries against complex backgrounds.",
  },
  {
    tip: "Correct sliding footsteps using floor tracking references.",
    reason: "Ground tracking helps restore believable contact with the floor.",
  },
  {
    tip: "Replace severely morphing eye details with tracked assets when necessary.",
    reason: "Stable eye geometry helps maintain believable gaze and facial continuity.",
  },
  {
    tip: "Smooth temporal transitions around morphing clothing boundaries.",
    reason: "Consistent edges prevent wardrobe details from visibly shifting.",
  },
  {
    tip: "Clean ghosting shadows behind fast-moving subjects.",
    reason: "Removing unwanted shadows restores cleaner subject separation.",
  },
  {
    tip: "Correct perspective warping along straight architectural lines.",
    reason: "Straight lines provide strong references for restoring spatial stability.",
  },
  {
    tip: "Use dynamic water overlays when generated water lacks natural movement.",
    reason: "Controlled overlays can add convincing surface motion.",
  },
  {
    tip: "Patch flickering hair edges on close character shots.",
    reason: "Hair edges are common sources of distracting temporal artifacts.",
  },
  {
    tip: "Reconnect disconnected prop lighting using localized light overlays.",
    reason: "Consistent lighting helps props integrate naturally with characters.",
  },
  {
    tip: "Use optical flow to fill small missing-motion gaps where appropriate.",
    reason: "Interpolation can smooth short frame discontinuities.",
  },
  {
    tip: "Feather compositing masks around integrated visual elements.",
    reason: "Soft edges prevent obvious hard lines between layers.",
  },
  {
    tip: "Correct unnatural reflections using tracked matte projections.",
    reason: "Tracked reflections keep surfaces aligned with scene geometry.",
  },
  {
    tip: "Clean unnatural mouth shapes before applying automated lip-sync.",
    reason: "Cleaner mouth geometry can improve synchronization accuracy.",
  },

  // Workflow & Asset Management
  {
    tip: "Organize assets by scene, shot number, version, and generation seed.",
    reason: "Structured organization makes assets easier to retrieve and manage.",
  },
  {
    tip: "Save prompts together with their corresponding generated videos.",
    reason: "Prompt history makes iteration and troubleshooting much easier.",
  },
  {
    tip: "Archive high-resolution still frames from successful generations.",
    reason: "Still references provide useful visual anchors for future shots.",
  },
  {
    tip: "Use strict naming conventions such as Scene_Shot_Version.",
    reason: "Consistent names prevent file confusion and media-management errors.",
  },
  {
    tip: "Run batch generations with multiple seed values when possible.",
    reason: "Different seeds provide options for selecting stable motion results.",
  },
  {
    tip: "Store active project files on fast SSD storage.",
    reason: "Fast storage improves playback and editing performance.",
  },
  {
    tip: "Keep untouched copies of original generation renders.",
    reason: "Originals allow corrections to be reset or reworked later.",
  },
  {
    tip: "Document prompt parameters that repeatedly cause artifacts.",
    reason: "An artifact log becomes a useful internal production reference.",
  },
  {
    tip: "Use consistent color-management settings across workstation displays.",
    reason: "Consistent display calibration improves color decision-making.",
  },
  {
    tip: "Test rough cuts with low-resolution proxies before final cleanup.",
    reason: "Early editing reveals pacing problems before expensive processing.",
  },
  {
    tip: "Build a reusable clean-plate library for recurring environments.",
    reason: "Reusable plates can significantly speed up VFX cleanup.",
  },
  {
    tip: "Pre-visualize shot framing with storyboard sketches.",
    reason: "Storyboards clarify composition before spending generation resources.",
  },
  {
    tip: "Set the editing project's frame rate before importing generated footage.",
    reason: "This avoids unwanted automatic frame conversion or interpolation.",
  },
  {
    tip: "Maintain character-specific prompt templates.",
    reason: "Reusable descriptions keep recurring characters visually consistent.",
  },
  {
    tip: "Export visual references at native resolution for external VFX tools.",
    reason: "Native resolution preserves tracking and masking precision.",
  },
  {
    tip: "Use version-control tools or tracking sheets for team projects.",
    reason: "Tracking systems keep everyone aligned on asset status and revisions.",
  },
  {
    tip: "Generate short preview tests before starting long generation jobs.",
    reason: "Preview tests catch prompt problems before consuming significant compute.",
  },
  {
    tip: "Maintain duplicate off-site backups for important project assets.",
    reason: "Off-site backups protect production work against hardware failures.",
  },
  {
    tip: "Standardize export preset templates across the editorial workflow.",
    reason: "Presets reduce mismatched formats and export settings.",
  },
  {
    tip: "Perform final review on properly calibrated reference displays.",
    reason: "Calibration helps ensure color, contrast, and image quality are delivered consistently.",
  },
];

export default PRO_TIPS;